import moment, { type Moment, type MomentInput } from 'moment'

import { useAppSelector } from '~/store/hooks'

import { ROAS_CUTOFF_DATE } from '../constants'

export type ConversionRateCutoffState = 'none' | 'mixed' | 'before'

/**
 * Classifica um único intervalo de datas em relação ao cutoff: 'none' quando
 * ele começa no cutoff ou depois, 'before' quando começa e termina antes, e
 * 'mixed' quando começa antes e termina no cutoff ou depois (o intervalo
 * atravessa o cutoff).
 */
const classifyRange = (
  start: MomentInput,
  end: MomentInput,
  cutoff: Moment
): ConversionRateCutoffState => {
  if (!moment(start).isBefore(cutoff, 'day')) return 'none'
  return moment(end).isBefore(cutoff, 'day') ? 'before' : 'mixed'
}

/**
 * Classifica o período selecionado em relação ao cutoff de mudança de
 * atribuição de conversão. Com `checkCompare: true`, o período de
 * comparação também é classificado e o resultado mais "severo" prevalece: 'mixed' (algum
 * intervalo atravessa o cutoff) tem prioridade sobre 'before' (algum
 * intervalo é inteiramente anterior ao cutoff), que por sua vez prevalece
 * sobre 'none'. Sem comparação (padrão), considera só o período principal —
 * mesma convenção de `useClickMetricsCutoffAlert`.
 */
export function useConversionRateCutoffState(
  options: { checkCompare?: boolean } = {}
): ConversionRateCutoffState {
  const { startDate, endDate, compareStartDate, compareEndDate } =
    useAppSelector(state => state.datepickerHeader)
  if (!startDate || !endDate) return 'none'

  const cutoff = moment(ROAS_CUTOFF_DATE, 'DD/MM/YYYY')
  const primaryState = classifyRange(startDate, endDate, cutoff)
  const compareRangeEnd = compareEndDate ?? compareStartDate
  const compareState =
    options.checkCompare && compareStartDate
      ? classifyRange(compareStartDate, compareRangeEnd, cutoff)
      : 'none'

  if (primaryState === 'mixed' || compareState === 'mixed') return 'mixed'
  if (primaryState === 'before' || compareState === 'before') return 'before'
  return 'none'
}
